<template>
  <div class="metro-confusion-wordcloud">
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>正在生成句子云图...</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>
    
    <div v-else class="wordcloud-content">
      <!-- 句子云图容器 -->
      <div class="wordcloud-container">
        <div ref="wordcloudRef" class="wordcloud-canvas"></div>
        <!-- 数据来源注释 -->
        <div class="data-source">
          数据来源：小红书
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick, computed, watch } from 'vue'

interface SentenceData {
  text: string
  weight: number
}

const loading = ref(true)
const error = ref('')
const wordcloudRef = ref<HTMLElement | null>(null)
const confusionData = ref<string[]>([])
const processedSentences = ref<SentenceData[]>([])

// 计算顶部句子
const topSentences = computed(() => {
  return processedSentences.value.slice(0, 30)
})

// 监听数据变化并自动重新渲染词云
watch(
  [processedSentences, wordcloudRef],
  async () => {
    if (processedSentences.value.length > 0 && wordcloudRef.value && !loading.value) {
      await nextTick()
      await generateWordCloud()
    }
  },
  { flush: 'post' }
)

// 获取句子颜色
const getSentenceColor = (index: number): string => {
  const colors = [
    '#E4002B', '#97D700', '#FFD100', '#461D84', '#944D9A',
    '#D40068', '#ED6F00', '#0094D8', '#87CAED', '#C6AFD4',
    '#871C2B', '#007A60', '#E999C0', '#626020', '#BCA886',
    '#98D1C0', '#BC796F', '#C4984F', '#F5AB78', '#009F65'
  ]
  return colors[index % colors.length]
}

// 处理文本数据 - 直接使用原句，按长度分配权重
const processSentenceData = (texts: string[]): SentenceData[] => {
  return texts.map((text, index) => ({
    text: text.trim(),
    weight: Math.max(8, Math.min(28, 30 - text.length * 0.2)) // 较短的句子权重更高，便于显示
  })).filter(item => item.text.length > 0)
}

// 生成词云图
const generateWordCloud = async () => {
  if (!wordcloudRef.value || processedSentences.value.length === 0) return
  
  try {
    // 如果wordcloud可用，使用它；否则降级到简单显示
    if (typeof window !== 'undefined' && (window as any).WordCloud) {
      await generateWordCloudWithLibrary()
    } else {
      // 尝试加载wordcloud库
      try {
        await loadWordCloudScript()
        await generateWordCloudWithLibrary()
      } catch {
        generateSimpleSentenceDisplay()
      }
    }
  } catch (err) {
    generateSimpleSentenceDisplay()
  }
}

// 加载wordcloud脚本
const loadWordCloudScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ((window as any).WordCloud) {
      resolve()
      return
    }
    
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/wordcloud@1.2.2/src/wordcloud2.js'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load WordCloud library'))
    document.head.appendChild(script)
  })
}

// 使用wordcloud库生成词云
const generateWordCloudWithLibrary = async () => {
  const container = wordcloudRef.value
  if (!container) return
  
  container.innerHTML = '' // 清空容器
  
  // 创建画布
  const canvas = document.createElement('canvas')
  canvas.width = 900
  canvas.height = 600
  canvas.style.maxWidth = '100%'
  canvas.style.height = 'auto'
  container.appendChild(canvas)
  
  // 准备词云数据 - 使用完整句子
  const sentenceList = topSentences.value.map((sentence) => [
    sentence.text, 
    sentence.weight
  ])
  
  // 配置词云参数
  const options = {
    list: sentenceList,
    gridSize: 2, // 更小的网格，让句子排布更紧密
    weightFactor: 1,
    fontFamily: '"Noto Sans SC", "Microsoft YaHei", sans-serif',
    color: () => {
      const colors = [
        '#E4002B', '#97D700', '#FFD100', '#461D84', '#944D9A',
        '#D40068', '#ED6F00', '#0094D8', '#87CAED', '#C6AFD4',
        '#871C2B', '#007A60', '#E999C0', '#626020', '#BCA886'
      ]
      return colors[Math.floor(Math.random() * colors.length)]
    },
    backgroundColor: 'transparent',
    rotateRatio: 0.05, // 几乎不旋转，让句子更易读
    minSize: 8,
    maxSize: 28,
    drawOutOfBound: false,
    shrinkToFit: true,
    shape: 'circle', // 固定使用圆形
    ellipticity: 0.65 // 圆形的椭圆度
  }
  
  // 生成词云
  ;(window as any).WordCloud(canvas, options)
  
}

// 降级方案：简单的句子显示
const generateSimpleSentenceDisplay = () => {
  if (!wordcloudRef.value) return
  
  const container = wordcloudRef.value
  container.innerHTML = ''
  
  const sentencesContainer = document.createElement('div')
  sentencesContainer.className = 'simple-sentences-display'
  sentencesContainer.style.cssText = `
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 30px;
    justify-content: center;
    align-items: center;
    min-height: 500px;
    line-height: 1.6;
  `
  
  topSentences.value.slice(0, 25).forEach((sentence, index) => {
    const sentenceElement = document.createElement('div')
    sentenceElement.textContent = sentence.text
    sentenceElement.style.cssText = `
      font-size: ${Math.max(12, Math.min(20, sentence.weight))}px;
      color: ${getSentenceColor(index)};
      font-weight: 500;
      font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
      padding: 12px 16px;
      margin: 6px;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.95);
      transition: all 0.3s ease;
      cursor: default;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
      border: 1px solid rgba(0, 0, 0, 0.1);
      max-width: 350px;
      word-break: break-word;
      text-align: center;
    `
    
    sentenceElement.addEventListener('mouseenter', () => {
      sentenceElement.style.transform = 'scale(1.05) translateY(-2px)'
      sentenceElement.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)'
      sentenceElement.style.zIndex = '10'
    })
    
    sentenceElement.addEventListener('mouseleave', () => {
      sentenceElement.style.transform = 'scale(1) translateY(0)'
      sentenceElement.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.1)'
      sentenceElement.style.zIndex = '1'
    })
    
    sentencesContainer.appendChild(sentenceElement)
  })
  
  container.appendChild(sentencesContainer)
}

// 加载数据
const loadData = async () => {
  try {
    loading.value = true
    
    const response = await fetch('/confusion.csv')
    
    if (!response.ok) {
      throw new Error('疑惑数据加载失败')
    }
    
    const csvText = await response.text()
    const lines = csvText.trim().split('\n')
    
    // 跳过表头，提取标题列
    const titles = lines.slice(1).filter(line => line.trim()).map(line => {
      // 简单的CSV解析
      return line.replace(/^"?([^"]*)"?$/, '$1').trim()
    })
    
    confusionData.value = titles
    processedSentences.value = processSentenceData(titles)
    
    
    if (processedSentences.value.length === 0) {
      throw new Error('没有有效的句子数据')
    }
    
    
  } catch (err) {
    error.value = `数据加载失败: ${err}`
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadData()
  
  // 如果数据加载完成且没有错误，确保词云渲染
  if (processedSentences.value.length > 0 && !error.value) {
    await nextTick()
    await generateWordCloud()
  }
})
</script>

<style scoped>
.metro-confusion-wordcloud {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* 加载和错误状态 */
.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
  color: #64748b;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 图表说明 */
.chart-description {
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(59, 130, 246, 0.05);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
}

.description-text {
  margin: 0;
  color: #475569;
  line-height: 1.6;
  font-size: 14px;
}

/* 形状选择器 */
.shape-selector {
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.selector-header h4 {
  margin: 0 0 16px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}

.shape-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
}

.shape-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.shape-option:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: translateY(-2px);
}

.shape-option.active {
  border-color: #3b82f6;
  background: #dbeafe;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
}

.shape-preview {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-icon {
  width: 32px;
  height: 32px;
  background: #6b7280;
  border-radius: 4px;
}

.shape-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

/* 词云图容器 */
.wordcloud-container {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

.wordcloud-canvas {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
}

.wordcloud-canvas canvas {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

/* 简单句子显示样式 */
.simple-sentences-display {
  border-radius: 8px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .shape-options {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .wordcloud-canvas {
    min-height: 400px;
  }
  
  .simple-sentences-display {
    padding: 20px !important;
    min-height: 400px !important;
  }
}
</style>